import type {
  CatalogueItem,
  CatalogueItemPresentationInput,
  SpecListEntry,
} from "../data/catalogue-types";
import type { FieldError } from "../data/validation";

// The customer-facing copy staff write on a catalogue item. The backend
// (`catalogue_module/models/item_input.py`) enforces these limits and is the
// authority; they are repeated here only so the editor can show the counters.
export const MAX_DESCRIPTION_LENGTH = 600;
export const MAX_SPEC_LIST_ENTRIES = 8;
export const MAX_SPEC_LABEL_LENGTH = 30;
export const MAX_SPEC_VALUE_LENGTH = 120;
export const MAX_RELATED_ITEMS = 4;

/**
 * The shared body of derive/update for an item as the editor holds it.
 *
 * `updateCatalogueItem` replaces the whole presentation, so every field must be
 * sent on every save. The copy fields carry the item's **own** values, never the
 * display values: the display item shows a parent's text when the item sets none,
 * and sending that back would copy it onto the item.
 */
export function presentationInputFrom(
  item: CatalogueItem,
): CatalogueItemPresentationInput {
  return {
    name: item.name,
    productLabel: item.productLabel,
    defaultQuantity: item.defaultQuantity,
    artworkModes: item.artworkModes,
    icon: item.ownIcon,
    specSummary: item.ownSpecSummary,
    description: item.ownDescription,
    specList: item.ownSpecList,
    relatedItemIds: item.relatedItemIds,
    note: item.note,
    sortOrder: item.sortOrder,
    priceGroupId: item.priceGroupId,
    vatTreatment: item.vatTreatment,
    narrowing: item.narrowing,
    customerProductRef: item.customerProductRef,
  };
}

/**
 * A new, unsaved item derived from `base`. It starts with nothing of its own
 * (so it inherits `base`'s copy) and no related items: `base`'s ids point into
 * its own catalogue, which may not be this one.
 */
export function newItemDerivedFrom(
  base: CatalogueItem,
  fields: Pick<CatalogueItem, "id" | "name" | "catalogueId" | "narrowing">,
): CatalogueItem {
  return {
    ...base,
    ...fields,
    derivesFromDictionaryItemId: undefined,
    derivesFromCatalogueItemId: base.id,
    ownIcon: undefined,
    ownSpecSummary: undefined,
    ownDescription: undefined,
    ownSpecList: [],
    relatedItemIds: [],
    routeHint: undefined,
    tharsternProvenance: undefined,
  };
}

/** What a customer-facing field would show if the item set nothing of its own. */
export interface InheritedCopy {
  fromName: string;
  description?: string;
  specList: SpecListEntry[];
}

/** The parent's display copy, or `null` when it has none to hand down. */
export function inheritedCopyFrom(
  parent: Pick<CatalogueItem, "name" | "description" | "specList"> | null,
): InheritedCopy | null {
  if (!parent) return null;
  const specList = parent.specList ?? [];
  if (!parent.description && specList.length === 0) return null;
  return {
    fromName: parent.name,
    description: parent.description,
    specList,
  };
}

/**
 * Field errors keyed by their request-body path (`description`,
 * `spec_list.0.label`, `related_item_ids.1`). Two errors on one field are
 * joined so neither is hidden.
 */
export function fieldErrorMessages(
  errors: readonly FieldError[],
): Record<string, string> {
  const byField: Record<string, string> = {};
  for (const e of errors) {
    byField[e.field] = byField[e.field]
      ? `${byField[e.field]} ${e.message}`
      : e.message;
  }
  return byField;
}

/** Move `list[from]` to `to`, returning a new list; out-of-range moves return `list` unchanged. */
export function moveListEntry<T>(
  list: readonly T[],
  from: number,
  to: number,
): T[] {
  if (from < 0 || from >= list.length || to < 0 || to >= list.length)
    return [...list];
  const next = [...list];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}
